import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { GlowButton } from "@/components/GlowButton";
import { GlassCard } from "@/components/GlassCard";
import { SectionHeader } from "@/components/SectionHeader";
import { CountUpStat } from "@/components/CountUpStat";
import { useSeo } from "@/hooks/useSeo";
import { STATS, VALUES } from "@/lib/data";

const container = "mx-auto max-w-6xl px-4 sm:px-6 lg:px-8";

const fadeUp = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  show: (i = 0) => ({ opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function About() {
  useSeo({
    title: "About — Métis Tech Labs",
    description:
      "Métis Tech Labs is an AI and software innovation company helping businesses automate operations, improve customer experiences, and build intelligent digital solutions.",
  });

  return (
    <div className="pt-32">
      <section className={container}>
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs uppercase tracking-[0.16em] text-neon-g">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--neon-g)] shadow-[0_0_10px_var(--neon-g)]" />
              Who we are
            </span>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-5xl">
              An AI & software innovation company for <span className="text-gradient">serious businesses</span>.
            </h1>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-brand">
              <p>
                Métis Tech Labs helps businesses automate operations, improve customer experiences,
                and build intelligent digital solutions that create a durable competitive edge.
              </p>
              <p>
                We don’t just build websites — we solve business problems through technology. Every
                engagement starts with your goals, then we engineer the systems that move the metrics
                that matter.
              </p>
              <p>
                Combining frontier AI with disciplined engineering, we deliver production-grade
                software that’s integrated with your stack and accountable to measurable outcomes.
              </p>
            </div>
            <div className="mt-8">
              <GlowButton as={Link} to="/contact" pulse data-testid="about-cta-button">
                Start a project <ArrowUpRight size={16} />
              </GlowButton>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} custom={1} initial="hidden" animate="show" className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((s, i) => (
                <GlassCard key={s.label} glow={i % 2 ? "purple" : "green"} className="relative p-5">
                  <span className="absolute right-4 top-4 h-1.5 w-1.5 rounded-full bg-[color:var(--neon-g)] shadow-[0_0_8px_var(--neon-g)]" />
                  <div className="font-display text-2xl font-semibold text-white sm:text-3xl">
                    <CountUpStat value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-1.5 text-xs text-muted-brand">{s.label}</div>
                </GlassCard>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className={`${container} mt-24`}>
        <SectionHeader
          eyebrow="What drives us"
          title="Solving business problems through technology"
          subtitle="The operating principles behind every intelligent system we deliver."
        />
        <div className="mt-12 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
              <GlassCard glow={i % 2 ? "purple" : "green"} className="flex h-full items-start gap-4 p-6">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl glass text-neon-g">
                  <v.icon size={20} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-brand">{v.desc}</p>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        <div className="mt-16">
          <div className="neon-frame relative overflow-hidden rounded-3xl glass-strong px-6 py-12 text-center sm:px-12">
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Let’s solve your hardest problem
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-muted-brand">
              Tell us where technology is holding you back. We’ll show you what’s possible.
            </p>
            <div className="mt-7 flex justify-center">
              <GlowButton as={Link} to="/contact" pulse data-testid="about-secondary-cta-button">
                Book Consultation <ArrowUpRight size={16} />
              </GlowButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
